// Merges every stored source into one Network, in the browser (no Worker CPU spent on Peter's data).
//
// Order: CRM board rows are the base list (or Google Contacts when there's no CRM). Google Contacts, LinkedIn,
// conference lists, the touch log and past-deal participation are matched onto it by email, then by name
// (nicknames folded: Bob = Robert), then by name + company when names collide. categorize() then works out each
// person's investor type, sectors, check size, private holds and why-now signals.

import { askedSectorsFromTouches, categorize, parseMoney } from "./categorize";
import {
  NETWORK_VERSION,
  REGIONS,
  SECTORS,
  contactKey,
  type Contact,
  type EventAttendance,
  type Flag,
  type Network,
  type PastDealRow,
  type PastPitch,
  type RecentTouch,
  type SourceKind,
  type SourceSummary,
} from "./network";
import type { CanonRow } from "./sources";

export interface BuildLogLine {
  tag: string; // short upper-case source tag: "MONDAY", "GOOGLE", "LINKEDIN", "CONF", "TOUCHES", "DEALS", "MERGE", "FLAGS"
  text: string;
  tone: "ok" | "warn" | "info";
}
export interface ConferenceLead {
  name: string;
  company: string;
  title: string;
  email: string;
  location: string;
  registration: string;
  event: string;
  date: string;
}
export interface IngestSource {
  meta: { id: string; kind: SourceKind; label: string; rows: number; event?: string; date?: string };
  rows: CanonRow[];
}
export interface BuildResult {
  network: Network;
  leads: ConferenceLead[]; // conference attendees not yet in the network
  log: BuildLogLine[];
}

export const EPOCH = "2000-01-01";

const NICK2FORMAL: Record<string, string> = {
  bob: "robert", rob: "robert", bobby: "robert", bill: "william", will: "william", billy: "william", rick: "richard", dick: "richard",
  rich: "richard", jim: "james", jimmy: "james", mike: "michael", tom: "thomas", tommy: "thomas", joe: "joseph", joey: "joseph",
  chuck: "charles", charlie: "charles", chris: "christopher", dan: "daniel", danny: "daniel", matt: "matthew", tony: "anthony",
  ed: "edward", eddie: "edward", ted: "edward", jon: "jonathan", steve: "stephen", ken: "kenneth", kenny: "kenneth", tim: "timothy",
  andy: "andrew", drew: "andrew", liz: "elizabeth", beth: "elizabeth", betsy: "elizabeth", kate: "katherine", katie: "katherine",
  kathy: "katherine", jen: "jennifer", jenny: "jennifer", peggy: "margaret", maggie: "margaret", meg: "margaret", trish: "patricia",
  patty: "patricia", ben: "benjamin", nick: "nicholas", pete: "peter", sam: "samuel", greg: "gregory", alex: "alexander",
  larry: "lawrence", jerry: "gerald", jeff: "jeffrey", fred: "frederick", hank: "henry", jack: "john", johnny: "john",
  dave: "david", doug: "douglas", don: "donald", ron: "ronald", ray: "raymond", phil: "philip", vince: "vincent", sue: "susan",
  debbie: "deborah", deb: "deborah", becky: "rebecca", vicky: "victoria", cindy: "cynthia", mandy: "amanda", abby: "abigail",
};

const STATE_CODES: Record<string, string> = {
  alabama: "AL", alaska: "AK", arizona: "AZ", arkansas: "AR", california: "CA", colorado: "CO", connecticut: "CT", delaware: "DE",
  florida: "FL", georgia: "GA", hawaii: "HI", idaho: "ID", illinois: "IL", indiana: "IN", iowa: "IA", kansas: "KS", kentucky: "KY",
  louisiana: "LA", maine: "ME", maryland: "MD", massachusetts: "MA", michigan: "MI", minnesota: "MN", mississippi: "MS",
  missouri: "MO", montana: "MT", nebraska: "NE", nevada: "NV", "new hampshire": "NH", "new jersey": "NJ", "new mexico": "NM",
  "new york": "NY", "north carolina": "NC", "north dakota": "ND", ohio: "OH", oklahoma: "OK", oregon: "OR", pennsylvania: "PA",
  "rhode island": "RI", "south carolina": "SC", "south dakota": "SD", tennessee: "TN", texas: "TX", utah: "UT", vermont: "VT",
  virginia: "VA", washington: "WA", "west virginia": "WV", wisconsin: "WI", wyoming: "WY", "district of columbia": "DC",
};
const REGION_OF: Record<string, string> = {};
const assign = (region: string, codes: string) => codes.split(" ").forEach((c) => (REGION_OF[c] = region));
assign("Mountain West", "CO WY UT MT ID");
assign("Southwest", "AZ NM NV OK");
assign("Midwest", "IL MO MN WI OH MI IN KS IA NE ND SD");
assign("Texas", "TX");
assign("Northeast", "NY CT MA NJ PA RI VT NH ME MD DE DC");
assign("West Coast", "CA WA OR AK HI");
assign("Southeast", "FL GA TN NC SC AL MS LA AR KY VA WV");

const MONTHS: Record<string, number> = { jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12 };
const pad = (n: number | string) => String(n).padStart(2, "0");

/** Reads the date formats exports use: 2026-03-14, 3/14/2026, 14 Mar 2026, Mar 14, 2026, Excel serials. "" when unreadable. */
export function isoDate(raw: string | undefined): string {
  const s = (raw ?? "").trim();
  if (!s) return "";
  let m = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(s);
  if (m) return `${m[1]}-${pad(m[2])}-${pad(m[3])}`;
  m = /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})/.exec(s);
  if (m) return `${m[3].length === 2 ? "20" + m[3] : m[3]}-${pad(m[1])}-${pad(m[2])}`;
  m = /^(\d{1,2})[ -]([A-Za-z]{3})[a-z]*[ -](\d{4})/.exec(s);
  if (m && MONTHS[m[2].toLowerCase()]) return `${m[3]}-${pad(MONTHS[m[2].toLowerCase()])}-${pad(m[1])}`;
  m = /^([A-Za-z]{3})[a-z]*\.? (\d{1,2}),? (\d{4})/.exec(s);
  if (m && MONTHS[m[1].toLowerCase()]) return `${m[3]}-${pad(MONTHS[m[1].toLowerCase()])}-${pad(m[2])}`;
  if (/^\d{5}(\.\d+)?$/.test(s)) {
    const n = +s;
    if (n > 20000 && n < 80000) return new Date(Date.UTC(1899, 11, 30) + Math.round(n) * 86400000).toISOString().slice(0, 10);
  }
  const t = Date.parse(s);
  return Number.isFinite(t) ? new Date(t).toISOString().slice(0, 10) : "";
}

const dayNum = (iso: string) => Math.round((Date.parse(iso + "T12:00:00Z") - Date.parse(EPOCH + "T12:00:00Z")) / 86400000);
const clean = (s: string | undefined) => (s ?? "").replace(/\s+/g, " ").trim();
const canonFirst = (f: string) => {
  const x = f.toLowerCase().replace(/[^a-z'-]/g, "");
  return NICK2FORMAL[x] ?? x;
};
const nameKey = (first: string, last: string) => `${canonFirst(first)}|${last.toLowerCase().replace(/[^a-z'-]/g, "")}`;
const emailsOf = (...vals: (string | undefined)[]) =>
  [...new Set(vals.flatMap((v) => (v ?? "").split(/[,;\s]+/)).map((e) => e.trim().toLowerCase()).filter((e) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)))];

function splitLocation(loc: string): { city: string; state: string } {
  const s = clean(loc);
  if (!s) return { city: "", state: "" };
  const parts = s.split(",").map((x) => x.trim()).filter(Boolean);
  const last = parts.length > 1 ? parts[parts.length - 1] : "";
  const code = stateCode(last);
  if (code) return { city: parts.slice(0, -1).join(", "), state: code };
  const tail = /^(.*?)\s+([A-Z]{2})$/.exec(s); // "Aspen CO"
  if (tail && REGION_OF[tail[2]]) return { city: tail[1], state: tail[2] };
  return { city: parts[0], state: "" };
}
function stateCode(s: string): string {
  const t = clean(s).replace(/\./g, "");
  if (/^[A-Za-z]{2}$/.test(t) && REGION_OF[t.toUpperCase()]) return t.toUpperCase();
  return STATE_CODES[t.toLowerCase()] ?? "";
}

function strengthOf(raw: string | undefined): number {
  const s = clean(raw);
  if (!s) return 0;
  const stars = (s.match(/⭐|★/g) ?? []).length;
  if (stars) return Math.min(5, stars);
  const n = parseFloat(s);
  if (Number.isFinite(n)) return Math.max(0, Math.min(5, Math.round(n)));
  const w = s.toLowerCase();
  if (/very (close|strong|warm)|inner circle|^a\b/.test(w)) return 5;
  if (/close|strong|warm|high/.test(w)) return 4;
  if (/medium|moderate|good/.test(w)) return 3;
  if (/weak|low|cold|acquaint/.test(w)) return 2;
  return 0;
}

interface Touch {
  date: string;
  channel: string;
  dir: "I" | "O";
  summary: string;
}
interface Person {
  name: string;
  first: string;
  last: string;
  emails: string[];
  phone: string;
  company: string;
  title: string;
  city: string;
  state: string;
  crmType: string;
  crmSectors: string;
  dealTypes: string[];
  checkMinRaw: string;
  checkMaxRaw: string;
  checkRaw: string;
  strength: number;
  howMet: string;
  status: string;
  notes: string[];
  sources: string[];
  group: string;
  linkedin: string;
  linkedinCompany: string;
  conferences: EventAttendance[];
  registrationTypes: string[];
  interests: string[];
  past: PastPitch[];
  touches: Touch[];
  lastContacted: string;
  pastDealIds: string[];
}

function newPerson(): Person {
  return {
    name: "", first: "", last: "", emails: [], phone: "", company: "", title: "", city: "", state: "", crmType: "", crmSectors: "",
    dealTypes: [], checkMinRaw: "", checkMaxRaw: "", checkRaw: "", strength: 0, howMet: "", status: "", notes: [], sources: [], group: "",
    linkedin: "", linkedinCompany: "", conferences: [], registrationTypes: [], interests: [], past: [], touches: [], lastContacted: "",
    pastDealIds: [],
  };
}

// Flag keys from categorize() (ours or the Rolodex thread's) folded onto the names the engine knows.
const FLAG_ALIASES: Record<string, string> = {
  "illness": "serious illness", "serious illness": "serious illness", "health": "serious illness", "sick": "serious illness",
  "loss of child": "loss of child", "child loss": "loss of child", "lost child": "loss of child", "deceased": "deceased", "death": "deceased",
  "divorce": "divorce", "separated": "divorce", "opted out": "opted out", "do not contact": "opted out", "dnc": "opted out",
  "tapped out": "tapped out", "fully allocated": "tapped out", "no dry powder": "tapped out",
  "retired": "retired", "done": "retired", "inactive": "inactive", "sold company": "sold company",
  "liquidity": "sold company", "exit": "sold company", "new role": "new role", "job change": "new role", "family milestone": "family milestone",
};
const normFlag = (key: string) => {
  const k = key.toLowerCase().replace(/[_-]+/g, " ").trim();
  return FLAG_ALIASES[k] ?? k;
};

export function buildNetwork(sources: IngestSource[], today: string): BuildResult {
  const report: Record<string, number> = {};
  const bump = (k: string, n = 1) => (report[k] = (report[k] ?? 0) + n);
  const log: BuildLogLine[] = [];
  const say = (tag: string, text: string, tone: BuildLogLine["tone"] = "ok") => log.push({ tag, text, tone });
  const of = (kind: SourceKind) => sources.filter((s) => s.meta.kind === kind);
  const fmt = (n: number) => n.toLocaleString("en-US");

  const people: Person[] = [];
  // An email can be shared (family members at one family office, a generic info@ address), so each email maps
  // to everyone who uses it and the name decides between them.
  const byEmail = new Map<string, Person[]>();
  const byName = new Map<string, Person[]>();
  const index = (p: Person) => {
    for (const e of p.emails) {
      const list = byEmail.get(e) ?? [];
      if (!list.includes(p)) list.push(p);
      byEmail.set(e, list);
    }
    if (p.first || p.last) {
      const k = nameKey(p.first, p.last);
      const list = byName.get(k) ?? [];
      if (!list.includes(p)) list.push(p);
      byName.set(k, list);
    }
  };
  const match = (emails: string[], first?: string, last?: string, company?: string): [Person | null, string] => {
    for (const e of emails) {
      const list = byEmail.get(e);
      if (!list) continue;
      if (list.length === 1) return [list[0], "email"];
      const nk = first || last ? nameKey(first ?? "", last ?? "") : "";
      const same = list.find((p) => nameKey(p.first, p.last) === nk);
      if (same) return [same, "email"];
      if (!nk) return [list[0], "email"];
    }
    if (first && last) {
      const cands = byName.get(nameKey(first, last)) ?? [];
      if (cands.length === 1) return [cands[0], "name"];
      if (cands.length > 1 && company) {
        const cc = cands.filter((c) => c.company.toLowerCase() === company.toLowerCase());
        if (cc.length === 1) return [cc[0], "name+company"];
      }
      if (cands.length > 1) bump("ambiguous_name_matches");
    }
    return [null, ""];
  };
  const addSource = (p: Person, s: string) => {
    if (!p.sources.includes(s)) p.sources.push(s);
  };
  const addNote = (p: Person, text: string, prefix = "") => {
    const t = clean(text);
    if (t && !p.notes.some((n) => n.includes(t))) p.notes.push(prefix + t);
  };

  // ---------- 1. Base list: CRM board rows ----------
  const crm = of("crm");
  for (const src of crm) {
    let added = 0;
    let merged = 0;
    for (const r of src.rows) {
      const emails = emailsOf(r.email);
      const first = clean(r.first);
      const last = clean(r.last);
      // The same person twice on the board (same email AND same name) is merged; anything less stays separate.
      let existing: Person | null = null;
      for (const e of emails) {
        existing = (byEmail.get(e) ?? []).find((x) => nameKey(x.first, x.last) === nameKey(first, last)) ?? null;
        if (existing) break;
      }
      const p = existing ?? newPerson();
      if (!existing) {
        p.name = clean(r.name) || `${first} ${last}`.trim();
        p.first = first;
        p.last = last;
        people.push(p);
        added++;
      } else merged++;
      for (const e of emails) if (!p.emails.includes(e)) p.emails.push(e);
      p.phone ||= clean(r.phone);
      p.company ||= clean(r.company);
      p.title ||= clean(r.title);
      const loc = r.location ? splitLocation(r.location) : { city: clean(r.city), state: stateCode(r.state ?? "") };
      p.city ||= loc.city;
      p.state ||= loc.state;
      p.crmType ||= clean(r.type);
      p.crmSectors ||= clean(r.sectors);
      if (r.dealTypes) p.dealTypes = [...new Set([...p.dealTypes, ...r.dealTypes.split(/[,;|]/).map((s) => s.trim()).filter(Boolean)])];
      p.checkMinRaw ||= clean(r.checkMin);
      p.checkMaxRaw ||= clean(r.checkMax);
      p.checkRaw ||= clean(r.check);
      p.strength = Math.max(p.strength, strengthOf(r.strength));
      p.howMet ||= clean(r.howMet);
      p.status ||= clean(r.status);
      p.group ||= clean(r.group);
      p.linkedin ||= clean(r.linkedin);
      p.lastContacted = [p.lastContacted, isoDate(r.lastContacted)].sort().pop() ?? "";
      if (r.pastDeals) p.pastDealIds.push(...r.pastDeals.split(/[,;|\s]+/).filter(Boolean));
      if (r.notes) addNote(p, r.notes);
      addSource(p, "Monday");
      index(p);
    }
    bump("crm_rows", src.rows.length);
    say("MONDAY", `${fmt(added)} people from ${src.meta.label.replace(/^Monday board: /, "")}${merged ? `, ${fmt(merged)} duplicate rows merged` : ""}`);
  }

  // ---------- 2. Google Contacts ----------
  const googleIsBase = crm.length === 0;
  for (const src of of("contacts")) {
    const how: Record<string, number> = {};
    let personal = 0;
    for (const r of src.rows) {
      const emails = emailsOf(r.email, r.email2);
      const first = clean(r.first);
      const last = clean(r.last);
      let [p, via] = match(emails, first, last, clean(r.company));
      if (!p) {
        if (!googleIsBase) {
          personal++;
          continue;
        }
        p = newPerson();
        p.name = clean(r.name) || `${first} ${last}`.trim();
        p.first = first;
        p.last = last;
        people.push(p);
        via = "new";
      }
      how[via] = (how[via] ?? 0) + 1;
      for (const e of emails) {
        if (!p.emails.includes(e)) {
          p.emails.push(e);
          bump("alt_emails_added");
        }
      }
      p.phone ||= clean(r.phone);
      p.company ||= clean(r.company);
      p.title ||= clean(r.title);
      p.city ||= clean(r.city);
      p.state ||= stateCode(r.state ?? "");
      p.linkedin ||= /linkedin\.com/i.test(r.linkedin ?? "") ? clean(r.linkedin) : "";
      if (r.notes) addNote(p, r.notes, googleIsBase ? "" : "Google note: ");
      addSource(p, "Google");
      index(p);
    }
    bump("google_personal_not_in_crm", personal);
    if (googleIsBase) say("GOOGLE", `${fmt(how.new ?? 0)} people from Google Contacts (no CRM board loaded, so Google is the base list)`, "warn");
    else
      say(
        "GOOGLE",
        `Matched ${fmt(how.email ?? 0)} by email, ${fmt((how.name ?? 0) + (how["name+company"] ?? 0))} by name; ${fmt(personal)} contacts not in the CRM were left out`,
      );
  }

  // ---------- 3. LinkedIn ----------
  for (const src of of("linkedin")) {
    let matched = 0;
    let unmatched = 0;
    let moved = 0;
    for (const r of src.rows) {
      const [p] = match(emailsOf(r.email), clean(r.first), clean(r.last), clean(r.company));
      if (!p) {
        unmatched++;
        continue;
      }
      matched++;
      p.linkedin ||= clean(r.linkedin);
      const lc = clean(r.company);
      if (lc && p.company && !lc.toLowerCase().includes(p.company.toLowerCase()) && !p.company.toLowerCase().includes(lc.toLowerCase())) {
        p.linkedinCompany = lc;
        moved++;
      }
      p.title ||= clean(r.title);
      addSource(p, "LinkedIn");
    }
    bump("linkedin_matched", matched);
    bump("linkedin_unmatched", unmatched);
    bump("linkedin_company_changed", moved);
    say("LINKEDIN", `Matched ${fmt(matched)} of ${fmt(src.rows.length)}; ${fmt(moved)} show a different company than the CRM`, moved ? "info" : "ok");
  }

  // ---------- 4. Conference lists ----------
  const leads: ConferenceLead[] = [];
  const events: Network["events"] = [];
  for (const src of of("conference")) {
    const name = src.meta.event || src.meta.label;
    const date = isoDate(src.meta.date) || "";
    events.push({ name, date });
    let known = 0;
    for (const r of src.rows) {
      const first = clean(r.first);
      const last = clean(r.last);
      const [p] = match(emailsOf(r.email), first, last, clean(r.company));
      if (p) {
        known++;
        if (!p.conferences.some((c) => c.name === name)) p.conferences.push({ name, date });
        if (r.registration) p.registrationTypes.push(clean(r.registration));
        if (r.interests) p.interests.push(clean(r.interests));
        addSource(p, "Conference");
      } else {
        leads.push({
          name: clean(r.name) || `${first} ${last}`.trim(), company: clean(r.company), title: clean(r.title), email: emailsOf(r.email)[0] ?? "",
          location: clean(r.location), registration: clean(r.registration), event: name, date,
        });
      }
    }
    bump("conference_matched", known);
    say("CONF", `${name}${date ? ` (${date})` : ""}: ${fmt(known)} people you know, ${fmt(src.rows.length - known)} new names`, date ? "ok" : "warn");
  }
  bump("conference_new_names", leads.length);

  // ---------- 5. Touch log ----------
  for (const src of of("touches")) {
    let matched = 0;
    let unmatched = 0;
    let badDate = 0;
    const who = new Set<Person>();
    for (const r of src.rows) {
      const date = isoDate(r.date);
      if (!date) {
        badDate++;
        continue;
      }
      const nm = clean(r.name).split(" ");
      const [p] = match(emailsOf(r.email), nm[0], nm.slice(1).join(" "));
      if (!p) {
        unmatched++;
        continue;
      }
      matched++;
      who.add(p);
      const d = (r.direction ?? "").toLowerCase();
      p.touches.push({ date, channel: clean(r.channel) || "Note", dir: /^in|received|from them|incoming/.test(d) ? "I" : "O", summary: clean(r.summary) });
      addSource(p, "Touch log");
    }
    bump("touches_matched", matched);
    bump("touch_unmatched", unmatched);
    say("TOUCHES", `${fmt(matched)} calls, texts and emails matched to ${fmt(who.size)} people${unmatched ? `; ${fmt(unmatched)} unmatched` : ""}${badDate ? `; ${fmt(badDate)} without a readable date` : ""}`, unmatched > matched ? "warn" : "ok");
  }

  // ---------- 6. Past deals and who invested ----------
  const pastDeals: PastDealRow[] = [];
  for (const src of of("deals"))
    for (const r of src.rows)
      pastDeals.push({
        deal_id: clean(r.dealId), name: clean(r.name) || clean(r.dealId),
        sectors: (r.sectors ?? "").split(/[|;,]/).map((s) => s.trim()).filter((s) => SECTORS.includes(s)).join("|") || clean(r.sectors),
        structure: clean(r.structure), raise_usd: String(parseMoney(r.raise) ?? ""), launch_date: isoDate(r.launch), close_date: isoDate(r.close),
        sponsor: clean(r.sponsor),
      });
  const launchOf = new Map(pastDeals.map((d) => [d.deal_id, d.launch_date]));
  const participation = of("participation");
  let pitches = 0;
  for (const src of participation)
    for (const r of src.rows) {
      const nm = clean(r.name).split(" ");
      const [p] = match(emailsOf(r.email), nm[0], nm.slice(1).join(" "));
      if (!p) continue;
      const o = (r.outcome ?? "").toLowerCase();
      const outcome = /invest|commit|funded|in\b|yes/.test(o) ? "Invested" : /pass|declin|no\b|not interested/.test(o) ? "Passed" : "No response";
      p.past.push({ deal: clean(r.dealId), outcome, amount: Math.round(parseMoney(r.amount) ?? 0), date: isoDate(r.date) || launchOf.get(clean(r.dealId)) || "" });
      pitches++;
    }
  if (!participation.length)
    for (const p of people)
      for (const id of p.pastDealIds) p.past.push({ deal: id, outcome: "Invested", amount: 0, date: launchOf.get(id) || "" });
  if (pastDeals.length || pitches) say("DEALS", `${fmt(pastDeals.length)} past deals, ${fmt(pitches)} pitches on record`);

  // ---------- 7. Categorize, then build the compact contacts ----------
  const contacts: Contact[] = [];
  const keys = new Set<string>();
  const typeSrc: Record<string, number> = {};
  let sensitive = 0;
  for (const p of people) {
    p.touches.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
    // The CRM's "Last contacted" counts as a touch when the touch log has nothing that recent.
    if (p.lastContacted && !p.touches.some((t) => t.date >= p.lastContacted)) {
      p.touches.push({ date: p.lastContacted, channel: "CRM", dir: "O", summary: "Last contacted (from the CRM)" });
      bump("touches_from_crm_last_contacted");
    }
    const asked = askedSectorsFromTouches(p.touches.filter((t) => t.dir === "I").map((t) => ({ date: t.date, summary: t.summary, direction: "Inbound" })));
    const investedSectors = [...new Set(p.past.filter((x) => x.outcome === "Invested").flatMap((x) => (pastDeals.find((d) => d.deal_id === x.deal)?.sectors ?? "").split("|")).filter(Boolean))];
    const notes = p.notes.join(" | ");
    const cat = categorize({
      title: p.title, company: p.company, notes, crmType: p.crmType, crmSectors: p.crmSectors, checkMinRaw: p.checkMinRaw,
      checkMaxRaw: p.checkMaxRaw, checkRaw: p.checkRaw, status: p.status, mondayGroup: p.group, registrationTypes: p.registrationTypes,
      interests: p.interests, askedSectors: asked.map((a) => a.sector), investedSectors, recentTouches: p.touches.map((t) => ({ date: t.date, summary: t.summary })),
    });
    const tys = cat.sources?.type ?? (p.crmType && cat.type !== "Unknown" ? "crm" : cat.type !== "Unknown" ? "notes" : "missing");
    const ses = cat.sources?.sectors ?? (cat.sectors.length ? (p.crmSectors ? "crm" : "notes") : "missing");
    const cs = cat.sources?.check ?? (p.checkMaxRaw || p.checkRaw ? "crm" : cat.checkMax ? "estimated from type" : "unknown");
    typeSrc[tys] = (typeSrc[tys] ?? 0) + 1;

    const flags: Flag[] = [];
    const addFlag = (flag: string, date: string | null, source: string, text: string) => {
      if (!flags.some((f) => f.flag === flag)) flags.push({ flag, date: date ? isoDate(date) || null : null, source, text: text.slice(0, 160) });
    };
    for (const h of cat.holds) addFlag(normFlag(h.key), h.date, h.date && p.touches.some((t) => t.summary === h.evidence) ? "touch log" : h.key === "inactive" ? "CRM status" : "notes", h.evidence);
    for (const w of cat.whyNow) addFlag(normFlag(w.key), w.date ?? null, "notes", w.evidence);
    if (["deceased", "loss of child", "serious illness", "divorce", "opted out"].some((f) => flags.some((x) => x.flag === f))) sensitive++;

    const burned = cat.sectorHolds.filter((h) => /burn/i.test(h.kind)).map((h) => h.sector);
    const justInv = cat.sectorHolds.filter((h) => /just|recent/i.test(h.kind));
    const ji = justInv.length ? { sector: justInv[0].sector, date: justInv[0].date ?? null } : null;

    // Strength: the CRM's, else estimated from how often they talk.
    let rs = p.strength;
    if (!rs && p.touches.length) {
      const yearAgo = new Date(Date.parse(today + "T12:00:00Z") - 365 * 86400000).toISOString().slice(0, 10);
      const n12 = p.touches.filter((t) => t.date >= yearAgo).length;
      rs = n12 >= 12 ? 5 : n12 >= 6 ? 4 : n12 >= 3 ? 3 : n12 >= 1 ? 2 : 1;
      bump("strength_estimated");
    }

    const primary = p.emails[0] ?? "";
    // A shared email can't identify one person, so those people are keyed by name and company instead.
    const shared = (byEmail.get(primary)?.length ?? 0) > 1;
    let k = contactKey(shared ? "" : primary, p.first, p.last, p.company);
    for (let i = 2; keys.has(k); i++) k = `${contactKey(shared ? "" : primary, p.first, p.last, p.company)}#${i}`;
    keys.add(k);
    const rt: RecentTouch[] = p.touches.slice(-5).map((t) => [t.date, t.channel, t.dir, t.summary]);
    contacts.push({
      id: "C" + String(contacts.length).padStart(5, "0"),
      k,
      n: p.name || `${p.first} ${p.last}`.trim() || primary,
      co: p.company,
      lco: p.linkedinCompany,
      ti: p.title,
      ty: cat.type,
      tys,
      ci: p.city,
      st: p.state,
      rg: REGION_OF[p.state] ?? (p.state ? "Other" : REGIONS[REGIONS.length - 1]),
      se: cat.sectors,
      ses,
      dt: p.dealTypes,
      cmin: Math.round(cat.checkMin || 0),
      cmax: Math.round(cat.checkMax || 0),
      cs,
      rs,
      hm: p.howMet,
      em: primary,
      ph: p.phone,
      no: notes,
      src: p.sources,
      li: p.linkedin,
      fl: flags,
      cf: p.conferences,
      pa: p.past,
      bu: burned,
      ji,
      ib: p.touches.filter((t) => t.dir === "I").map((t) => dayNum(t.date)),
      ob: p.touches.filter((t) => t.dir === "O").map((t) => dayNum(t.date)),
      rt,
      np: cat.negativePrefs.length ? cat.negativePrefs : undefined,
      as: asked.length ? asked : undefined,
    });
  }
  report.people = contacts.length;
  report.flagged_sensitive = sensitive;
  for (const [k, v] of Object.entries(typeSrc)) report[`type_${k}`] = v;
  say("TYPES", `Investor type from the CRM for ${fmt(typeSrc.crm ?? 0)}, read from notes for ${fmt(typeSrc.notes ?? 0)}, unknown for ${fmt(typeSrc.missing ?? 0)}`, (typeSrc.missing ?? 0) > contacts.length / 5 ? "warn" : "ok");
  say("FLAGS", `${fmt(sensitive)} people on hold for private reasons (a death, illness, divorce, or asked not to be contacted)`, "info");
  if (!contacts.length) say("MERGE", "No people found. Load a CRM board or Google Contacts file first.", "warn");
  else say("MERGE", `${fmt(contacts.length)} people, as of ${today}`);

  const summaries: SourceSummary[] = sources.map((s) => ({ id: s.meta.id, kind: s.meta.kind, label: s.meta.label, rows: s.rows.length }));
  const network: Network = {
    version: NETWORK_VERSION,
    generated: today,
    epoch: EPOCH,
    contacts,
    past_deals: pastDeals,
    events,
    sectors: SECTORS,
    report,
    sources: summaries,
  };
  return { network, leads, log };
}
